import React from 'react';
import { Divider } from '@chakra-ui/layout';
import { useLocation } from 'react-router-dom';
import { PreviewSection } from '../PreviewSection';
import moment from 'moment';
import { TravelFormQuery } from 'api/types/types';
import { Alert } from 'components/Alert/Alert';
import { LoadingContainer } from 'components/LoadingContainer/LoadingContainer';

type TravelRequestPreviewProps = {
  travelRequestData: TravelFormQuery | undefined;
  isLoadingRequestById: boolean;
};

export const TravelRequestPreview = ({
  travelRequestData,
  isLoadingRequestById,
}: TravelRequestPreviewProps): JSX.Element => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const id = queryParams.get('id');

  const notFound = travelRequestData?.message;

  return (
    <LoadingContainer isLoaded={!isLoadingRequestById} hasSpinner>
      {notFound ? (
        <Alert
          errorText={`Ferðabeiðni númer ${id} fannst því miður ekki`}
          status="warning"
        />
      ) : (
        <>
          <PreviewSection
            title="Grunnupplýsingar"
            values={[
              {
                Starfsmaður: travelRequestData?.employeeName,
                Samþykkjandi: travelRequestData?.approverName,
              },
            ]}
          />
          <Divider marginTop={10} marginBottom={10} />
          <PreviewSection
            title="Tilgangur ferðar"
            values={[
              {
                Erindi: travelRequestData?.tripErrand,
                'Tilgangur ferðar': travelRequestData?.tripPurpose,
              },
            ]}
          />
          <Divider marginTop={10} marginBottom={10} />
          <PreviewSection
            title="Ferðaupplýsingar"
            values={[
              {
                Land: travelRequestData?.country,
                Borg: travelRequestData?.city,
                Brottför: `${moment(travelRequestData?.departureDate).format(
                  'DD.MM.YYYY'
                )}${
                  travelRequestData?.departureTime
                    ? ` - ${travelRequestData?.departureTime}`
                    : ''
                }`,
                Heimkoma: `${moment(travelRequestData?.arrivalDate).format(
                  'DD.MM.YYYY'
                )}${
                  travelRequestData?.arrivalTime
                    ? ` - ${travelRequestData?.arrivalTime}`
                    : ''
                }`,

                Ferðatilhögun: travelRequestData?.travelArrangements,
                Aukadagar:
                  travelRequestData?.addExtraDays === 0
                    ? 'Öll ferðin á vegum Landsvirkjunar'
                    : `Auka dagar á eigin vegum: ${travelRequestData?.addExtraDays}`,
                Dagpeningar:
                  travelRequestData?.perDiemOption === 'fullir-dagpeningar'
                    ? 'Starfsmaður fær fulla dagpeninga'
                    : 'Starfsmaður fær dagpeninga fyrir öðru en gistingu',
              },
            ]}
          />
          <Divider marginTop={10} marginBottom={10} />
          <PreviewSection
            title="Flugbókun"
            values={[
              {
                Flugbókun: travelRequestData?.arrangeFlight
                  ? 'Starfsmaður vill láta bóka flug fyrir sig'
                  : 'Starfsmaður bókar sjálfur flug fyrir sig',
                Farangursóskir: travelRequestData?.baggageRequirements,
              },
            ]}
          />
          <Divider marginTop={10} marginBottom={10} />
          <PreviewSection
            title="Hótelgisting"
            values={[
              {
                Bókun: travelRequestData?.arrangeAccommodation
                  ? 'Starfsmaður vill láta bóka hótel fyrir sig'
                  : 'Starfsmaður bókar sjálfur hótel fyrir sig',
                Hóteltillaga: travelRequestData?.accommodationDetails,
              },
            ]}
          />
          <Divider marginTop={10} marginBottom={10} />
          <PreviewSection
            title="Bókhaldsupplýsingar"
            values={[
              {
                Verk: `${travelRequestData?.workProject} (${travelRequestData?.workProjectId}) `,
                Verkbeiðni: travelRequestData?.workOrder
                  ? `${travelRequestData?.workOrder} (${travelRequestData?.workOrderId})`
                  : '',
                'Deild verks': `${travelRequestData?.workDepartment} (${travelRequestData?.workDepartmentId}) `,
                'Staðsetning verks': `${travelRequestData?.workLocation} (${travelRequestData?.workLocationId})`,
              },
            ]}
          />
        </>
      )}
    </LoadingContainer>
  );
};
